import { catalogs, type TranslationKey } from '@stm/i18n';

const errorKeys: ReadonlySet<string> = new Set(
  Object.keys(catalogs.en).filter((key) => key.startsWith('error.')),
);

/** Failure from the API (or the network) carrying a translation key the UI can show. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly messageKey: TranslationKey,
  ) {
    super(code);
    this.name = 'ApiRequestError';
  }
}

interface ErrorBody {
  error?: { code?: unknown; messageKey?: unknown };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Required for every state-changing request. */
  csrfToken?: string | undefined;
  /** Row version the caller saw, sent as `If-Match` on state transitions. */
  ifMatchVersion?: number | undefined;
  /** Makes a retry of the same action safe; send the same key when repeating one request. */
  idempotencyKey?: string | undefined;
}

/**
 * Calls the same-origin API. Failures always become {@link ApiRequestError}; an unknown or missing
 * message key falls back to a generic translated message so raw server text is never shown.
 */
export async function apiRequest<Response>(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.csrfToken !== undefined) headers['x-csrf-token'] = options.csrfToken;
  if (options.idempotencyKey !== undefined) headers['idempotency-key'] = options.idempotencyKey;
  if (options.ifMatchVersion !== undefined)
    headers['if-match'] = `"${String(options.ifMatchVersion)}"`;

  let response: globalThis.Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'same-origin',
      cache: 'no-store',
      body: options.body === undefined ? null : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiRequestError(0, 'NETWORK_ERROR', 'error.unexpected');
  }

  if (response.ok) {
    if (response.status === 204) return undefined as Response;
    return (await response.json()) as Response;
  }

  let body: ErrorBody = {};
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    // Not a JSON error body (for example a proxy error page); the generic message applies.
  }
  const code = typeof body.error?.code === 'string' ? body.error.code : 'UNEXPECTED';
  const key = body.error?.messageKey;
  throw new ApiRequestError(
    response.status,
    code,
    typeof key === 'string' && isErrorKey(key) ? key : 'error.unexpected',
  );
}

export function isErrorKey(candidate: string): candidate is TranslationKey {
  return errorKeys.has(candidate);
}
